/**
 * Review store — spec §Store additions.
 *
 * Keeps review state separate from agent-store (different lifecycle).
 */

import { create } from 'zustand'
import * as reviewApi from '@/lib/review-api'
import type {
  ReviewCriterion,
  ReviewRunSummary,
  MergedReviewFinding,
  IssueActionStatus,
  LatestCheck,
  ReviewPermissions,
  StartReviewRunInput,
  CreateReviewCriterionInput,
  UpdateReviewCriterionInput,
} from '@/lib/schemas'

export type ReviewScope = 'artifact' | 'section'
// Either a runId or the literal sentinel "active" (currently-running selection)
export type SelectedThread = string

export interface ReviewRunUpdate {
  runId: string
  status: 'running' | 'complete' | 'failed'
  progress?: { current: number; total: number }
  /** The run's stored note, e.g. "Couldn't check: Compliance. Run again." */
  errorMessage?: string | null
}

/**
 * The run this session started, kept so the panel can say what it could not
 * check. Scoped to its response and section: a note about one response must
 * never show on another.
 */
export interface LastReviewRun {
  runId: string
  artifactId: string
  sectionKey: string | null
  status: 'running' | 'complete' | 'failed'
  errorMessage: string | null
}

export interface ReviewFindingActionUpdate {
  issueKey: string
  status: IssueActionStatus
}

interface ReviewStoreState {
  // Current scope
  scope: ReviewScope
  sectionKey: string | null

  // Criteria
  criteria: { system: ReviewCriterion[]; mine: ReviewCriterion[] } | null
  selectedCriteriaIds: string[]
  criteriaLoading: boolean

  // Permissions (per bid + artifact + optional section)
  permissions: ReviewPermissions | null
  permissionsLoading: boolean

  // Thread picker
  selectedThreadId: SelectedThread
  runHistory: ReviewRunSummary[]
  includeDismissed: boolean
  historyLoading: boolean

  // Active findings (merged view)
  activeFindings: MergedReviewFinding[] | null
  activeFindingsLoading: boolean

  // Latest check run for the artifact (+ staleness)
  latestCheck: LatestCheck | null
  latestCheckLoading: boolean

  // In-flight run
  activeRunId: string | null
  activeRunStatus: 'idle' | 'running' | 'complete' | 'failed'
  progress: { current: number; total: number } | null
  lastRun: LastReviewRun | null

  // Generic error
  error: string | null

  // -------------------
  // Actions — criteria
  // -------------------
  loadCriteria: () => Promise<void>
  createCriterion: (input: CreateReviewCriterionInput) => Promise<void>
  updateCriterion: (id: string, input: UpdateReviewCriterionInput) => Promise<void>
  deleteCriterion: (id: string) => Promise<void>
  toggleCriterion: (id: string) => void
  clearSelectedCriteria: () => void

  // -------------------
  // Actions — permissions
  // -------------------
  loadPermissions: (bidId: string, artifactId: string, sectionKey?: string | null) => Promise<void>

  // -------------------
  // Actions — scope / selection
  // -------------------
  setScope: (scope: ReviewScope, sectionKey?: string | null) => void
  selectThread: (threadId: SelectedThread) => void
  toggleDismissed: () => void

  // -------------------
  // Actions — runs
  // -------------------
  loadActiveFindings: (artifactId: string) => Promise<void>
  loadLatestCheck: (artifactId: string) => Promise<void>
  loadHistory: (artifactId: string) => Promise<void>
  startReview: (input: StartReviewRunInput) => Promise<string | null>
  cancelReview: (runId: string) => Promise<void>
  dismissRun: (runId: string) => Promise<void>
  restoreRun: (runId: string) => Promise<void>
  dismissAllFailed: (artifactId: string) => Promise<number>

  // -------------------
  // Actions — WebSocket / polling
  // -------------------
  handleWorkerUpdate: (update: ReviewRunUpdate) => void
  handleActionUpdate: (update: ReviewFindingActionUpdate) => void

  // Issue-level actions — keyed by the stable issue identity, not (run, index)
  dismissFinding: (artifactId: string, issueKey: string, reason?: string) => Promise<void>
  // Manual "Mark as fixed" / "Reopen" — for issues fixed by hand in the editor
  markFindingFixed: (artifactId: string, issueKey: string, fixed: boolean) => Promise<void>

  // Fix-with-AI closed loop: POST the action, return thread seed for the Ask AI (`write`) tab
  startFix: (artifactId: string, issueKey: string) => Promise<{ threadId: string; threadSeed: string } | null>

  // -------------------
  // Reset
  // -------------------
  reset: () => void
}

const initialState = {
  scope: 'artifact' as ReviewScope,
  sectionKey: null as string | null,
  criteria: null,
  selectedCriteriaIds: [] as string[],
  criteriaLoading: false,
  permissions: null as ReviewPermissions | null,
  permissionsLoading: false,
  selectedThreadId: 'active' as SelectedThread,
  runHistory: [] as ReviewRunSummary[],
  includeDismissed: false,
  historyLoading: false,
  activeFindings: null as MergedReviewFinding[] | null,
  activeFindingsLoading: false,
  latestCheck: null as LatestCheck | null,
  latestCheckLoading: false,
  activeRunId: null as string | null,
  activeRunStatus: 'idle' as 'idle' | 'running' | 'complete' | 'failed',
  progress: null as { current: number; total: number } | null,
  lastRun: null as LastReviewRun | null,
  error: null as string | null,
}

/**
 * Monotonic sequence for active-findings loads. A load only commits its
 * response if nothing superseded it while it was in flight: a newer load, or a
 * mutation (dismiss, mark fixed, action update). Without this, the second of
 * two mount-time fetches — the reader page and its Checks tab both load — lands
 * after an optimistic dismiss and silently puts the issue back.
 */
let activeFindingsSeq = 0

export const useReviewStore = create<ReviewStoreState>()((set, get) => ({
  ...initialState,

  // -------------------
  // Criteria
  // -------------------
  loadCriteria: async () => {
    set({ criteriaLoading: true, error: null })
    try {
      const criteria = await reviewApi.listCriteria()
      set({ criteria, criteriaLoading: false })
    } catch (err) {
      set({ error: (err as Error).message, criteriaLoading: false })
    }
  },

  createCriterion: async (input) => {
    try {
      const created = await reviewApi.createCriterion(input)
      const current = get().criteria
      if (current) {
        set({ criteria: { system: current.system, mine: [created, ...current.mine] } })
      } else {
        await get().loadCriteria()
      }
    } catch (err) {
      set({ error: (err as Error).message })
      throw err
    }
  },

  updateCriterion: async (id, input) => {
    try {
      const updated = await reviewApi.updateCriterion(id, input)
      const current = get().criteria
      if (current) {
        set({
          criteria: {
            system: current.system,
            mine: current.mine.map((c) => (c.id === id ? updated : c)),
          },
        })
      }
    } catch (err) {
      set({ error: (err as Error).message })
      throw err
    }
  },

  deleteCriterion: async (id) => {
    try {
      await reviewApi.deleteCriterion(id)
      const current = get().criteria
      if (current) {
        set({
          criteria: {
            system: current.system,
            mine: current.mine.filter((c) => c.id !== id),
          },
          selectedCriteriaIds: get().selectedCriteriaIds.filter((cid) => cid !== id),
        })
      }
    } catch (err) {
      set({ error: (err as Error).message })
      throw err
    }
  },

  toggleCriterion: (id) => {
    const current = get().selectedCriteriaIds
    set({
      selectedCriteriaIds: current.includes(id)
        ? current.filter((cid) => cid !== id)
        : [...current, id],
    })
  },

  clearSelectedCriteria: () => set({ selectedCriteriaIds: [] }),

  // -------------------
  // Permissions
  // -------------------
  loadPermissions: async (bidId, artifactId, sectionKey) => {
    set({ permissionsLoading: true })
    try {
      const permissions = await reviewApi.getReviewPermissions(bidId, artifactId, sectionKey ?? undefined)
      set({ permissions, permissionsLoading: false })
    } catch (err) {
      set({ permissionsLoading: false, error: (err as Error).message })
    }
  },

  // -------------------
  // Scope
  // -------------------
  setScope: (scope, sectionKey) => {
    set({ scope, sectionKey: scope === 'section' ? (sectionKey ?? null) : null })
  },

  selectThread: (threadId) => set({ selectedThreadId: threadId }),

  toggleDismissed: () => set({ includeDismissed: !get().includeDismissed }),

  // -------------------
  // Runs
  // -------------------
  loadActiveFindings: async (artifactId) => {
    const seq = ++activeFindingsSeq
    set({ activeFindingsLoading: true })
    try {
      const { findings } = await reviewApi.getActiveFindings(artifactId)
      // Superseded by a newer load or a mutation while in flight — drop it.
      if (seq !== activeFindingsSeq) return
      set({ activeFindings: findings, activeFindingsLoading: false })
    } catch (err) {
      if (seq !== activeFindingsSeq) return
      set({ activeFindingsLoading: false, error: (err as Error).message })
    }
  },

  loadLatestCheck: async (artifactId) => {
    set({ latestCheckLoading: true })
    try {
      const latestCheck = await reviewApi.getLatestCheck(artifactId)
      set({ latestCheck, latestCheckLoading: false })
    } catch (err) {
      set({ latestCheckLoading: false, error: (err as Error).message })
    }
  },

  loadHistory: async (artifactId) => {
    set({ historyLoading: true })
    try {
      const { runs } = await reviewApi.listRuns(artifactId, {
        includeDismissed: get().includeDismissed,
      })
      set({ runHistory: runs, historyLoading: false })
    } catch (err) {
      set({ historyLoading: false, error: (err as Error).message })
    }
  },

  startReview: async (input) => {
    try {
      const { runId } = await reviewApi.startRun(input)
      set({
        activeRunId: runId,
        activeRunStatus: 'running',
        progress: null,
        selectedThreadId: runId,
        lastRun: {
          runId,
          artifactId: input.artifactId,
          sectionKey: input.sectionKey ?? null,
          status: 'running',
          errorMessage: null,
        },
        error: null,
      })
      return runId
    } catch (err) {
      set({ error: (err as Error).message, activeRunStatus: 'failed' })
      return null
    }
  },

  cancelReview: async (runId) => {
    try {
      await reviewApi.cancelRun(runId)
      if (get().activeRunId === runId) {
        set({ activeRunStatus: 'failed', progress: null })
      }
      const lastRun = get().lastRun
      if (lastRun?.runId === runId) {
        set({ lastRun: { ...lastRun, status: 'failed', errorMessage: null } })
      }
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  dismissRun: async (runId) => {
    // Optimistic: mark dismissed in history.
    const prev = get().runHistory
    const now = new Date().toISOString()
    set({
      runHistory: prev.map((r) => (r.id === runId ? { ...r, dismissedAt: now } : r)),
    })
    try {
      await reviewApi.dismissRun(runId)
    } catch (err) {
      set({ runHistory: prev, error: (err as Error).message })
    }
  },

  restoreRun: async (runId) => {
    const prev = get().runHistory
    set({
      runHistory: prev.map((r) => (r.id === runId ? { ...r, dismissedAt: null } : r)),
    })
    try {
      await reviewApi.restoreRun(runId)
    } catch (err) {
      set({ runHistory: prev, error: (err as Error).message })
    }
  },

  dismissAllFailed: async (artifactId) => {
    try {
      const { dismissed } = await reviewApi.dismissAllFailed(artifactId)
      await get().loadHistory(artifactId)
      return dismissed
    } catch (err) {
      set({ error: (err as Error).message })
      return 0
    }
  },

  // -------------------
  // WebSocket handlers (fire on polling callbacks too)
  // -------------------
  handleWorkerUpdate: (update) => {
    if (get().activeRunId === update.runId) {
      set({
        activeRunStatus: update.status,
        progress: update.progress ?? null,
      })
    }
    // The run's note is a partial result, not an app error: it goes on the
    // run, never into the generic error banner.
    const lastRun = get().lastRun
    if (lastRun?.runId === update.runId) {
      set({
        lastRun: { ...lastRun, status: update.status, errorMessage: update.errorMessage ?? null },
      })
    }
  },

  handleActionUpdate: (update) => {
    const current = get().activeFindings
    if (!current) return
    activeFindingsSeq++
    set({
      activeFindings: current.map((f) =>
        f.issueKey === update.issueKey ? { ...f, actionStatus: update.status } : f,
      ),
    })
  },

  dismissFinding: async (artifactId, issueKey, reason) => {
    const prev = get().activeFindings
    activeFindingsSeq++
    if (prev) {
      set({ activeFindings: prev.filter((f) => f.issueKey !== issueKey) })
    }
    try {
      await reviewApi.dismissIssue(artifactId, issueKey, reason)
    } catch (err) {
      set({ activeFindings: prev, error: (err as Error).message })
    }
  },

  markFindingFixed: async (artifactId, issueKey, fixed) => {
    const prev = get().activeFindings
    const status = fixed ? ('applied' as const) : ('open' as const)
    // Optimistic — the finding stays visible either way, only the chip changes.
    get().handleActionUpdate({ issueKey, status })
    try {
      await reviewApi.setIssueActionStatus(artifactId, issueKey, status)
    } catch (err) {
      set({ activeFindings: prev, error: (err as Error).message })
    }
  },

  startFix: async (artifactId, issueKey) => {
    try {
      const response = await reviewApi.startFixAction(artifactId, issueKey)
      // Optimistic status update — the WebSocket / polling pass will confirm.
      get().handleActionUpdate({ issueKey, status: 'addressing' })
      return response
    } catch (err) {
      set({ error: (err as Error).message })
      return null
    }
  },

  // -------------------
  // Reset
  // -------------------
  reset: () => {
    activeFindingsSeq++
    set({ ...initialState })
  },
}))
